import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DurationUnit, splitIsoDuration, toIsoDuration } from '@netfoundry-ui/util/duration';
import { isNil } from 'lodash';

/** The unit an empty box starts in. Offline ladders are usually hours. */
const DEFAULT_UNIT: DurationUnit = 'h';

/**
 * One duration as a number box and a unit select, bound as an ISO-8601 string. A box that holds no positive number
 * emits `null` and reports itself invalid, so the parent never holds a typed number.
 */
@Component({
    selector: 'app-duration-input',
    templateUrl: './duration-input.component.html',
    styleUrls: ['./duration-input.component.scss'],
    standalone: false,
})
export class DurationInputComponent implements OnChanges {
    @Input() value?: string | null;
    @Input() placeholder = '';
    @Input() disabled = false;
    /** Outlines the box, for a message the parent shows elsewhere. */
    @Input() invalid = false;
    @Input() inputId = '';
    @Input() label = '';
    /** A narrower layout, for a table cell. */
    @Input() compact = false;

    @Output() valueChange = new EventEmitter<string | null>();
    @Output() blurred = new EventEmitter<void>();
    @Output() invalidChange = new EventEmitter<boolean>();

    readonly units: { value: DurationUnit; label: string }[] = [
        { value: 'min', label: 'Minutes' },
        { value: 'h', label: 'Hours' },
        { value: 'd', label: 'Days' },
    ];

    amount: number | null = null;
    unit: DurationUnit = DEFAULT_UNIT;

    private emitted?: string | null;
    private wasInvalid = false;

    /** True while the box holds something that is not a positive number. */
    get isInvalid(): boolean {
        return !isNil(this.amount) && isNil(toIsoDuration(this.amount, this.unit));
    }

    ngOnChanges(changes: SimpleChanges): void {
        /* The value this control emitted comes straight back, and re-splitting it would turn a typed `90 min` into
         * `1.5 h` under the reader's cursor. */
        if (changes['value'] && (this.value || null) !== (this.emitted ?? null)) {
            const parts = splitIsoDuration(this.value);
            this.amount = parts?.value ?? null;
            this.unit = parts?.unit ?? this.unit;
            this.emitted = this.value || null;
            this.reportValidity();
        }
    }

    setAmount(value: number | string | null): void {
        this.amount = isNil(value) || `${value}`.trim() === '' ? null : Number(value);
        this.emit();
    }

    setUnit(unit: DurationUnit): void {
        this.unit = unit;
        this.emit();
    }

    private emit(): void {
        const iso = isNil(this.amount) ? null : (toIsoDuration(this.amount, this.unit) ?? null);
        this.emitted = iso;
        this.valueChange.emit(iso);
        this.reportValidity();
    }

    private reportValidity(): void {
        const invalid = this.isInvalid;
        if (invalid !== this.wasInvalid) {
            this.wasInvalid = invalid;
            this.invalidChange.emit(invalid);
        }
    }
}
